import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/store/useAppStore'

// ─── Types ────────────────────────────────────────────────────────────────────

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete' | 'paused'
export type SubscriptionPlan   = 'solo' | 'pro' | 'trial' | 'free'

export interface Subscription {
  id:                     string
  user_id:                string
  stripe_customer_id:     string | null
  stripe_subscription_id: string | null
  stripe_price_id:        string | null
  plan:                   SubscriptionPlan
  billing_interval:       'month' | 'year'
  status:                 SubscriptionStatus
  trial_start:            string | null
  trial_end:              string | null
  current_period_start:   string | null
  current_period_end:     string | null
  canceled_at:            string | null
  cancel_at_period_end:   boolean
  created_at:             string
  updated_at:             string
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSubscription() {
  const { user }                        = useAppStore()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading]           = useState(true)
  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return }
    const { data } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .single()
    setSubscription(data as Subscription | null)
    setLoading(false)
  }, [user?.id])

  useEffect(() => { fetch() }, [fetch])

  // Realtime : mise à jour immédiate quand le webhook Stripe modifie l'abonnement
  useEffect(() => {
    if (!user) return
    // Nom généré à chaque exécution de l'effet — jamais réutilisé depuis le cache Supabase
    const channelName = `subscription-rt-${Math.random().toString(36).slice(2)}`
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'subscriptions', filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (payload.new) setSubscription(payload.new as Subscription)
        },
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [user?.id])

  // ── Statuts calculés ───────────────────────────────────────────────────────

  const isTrialing  = subscription?.status === 'trialing'
  const isActive    = subscription?.status === 'active'
  const isPastDue   = subscription?.status === 'past_due'
  const isCanceled  = subscription?.status === 'canceled'
  const hasAccess   = isTrialing || isActive

  const trialDaysLeft = subscription?.trial_end
    ? Math.max(0, Math.ceil(
        (new Date(subscription.trial_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      ))
    : 0

  // ── Actions ────────────────────────────────────────────────────────────────

  const startCheckout = async (interval: 'month' | 'year') => {
    // /pricing est une page publique — vérifier que l'utilisateur est bien connecté
    // avant d'appeler l'Edge Function, sinon supabase.functions.invoke() envoie
    // la clé anon au lieu du JWT de session → "This endpoint requires a valid Bearer token"
    if (!user) {
      window.location.href = `/login?redirect=${encodeURIComponent('/pricing')}`
      return
    }

    // Double-check : récupérer la session active pour s'assurer que le token est valide
    const { data: sessionData } = await supabase.auth.getSession()
    if (!sessionData.session?.access_token) {
      window.location.href = `/login?redirect=${encodeURIComponent('/pricing')}`
      return
    }

    const priceId = getPriceId(interval)

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: { priceId, interval },
    })

    if (error || data?.error) {
      throw new Error(data?.error ?? error?.message ?? 'Erreur checkout')
    }

    if (data?.code === 'RATE_LIMIT_EXCEEDED') {
      throw new Error('Trop de tentatives. Réessayez dans quelques minutes.')
    }

    if (!data?.url) throw new Error('URL de paiement manquante')
    window.location.href = data.url
  }

  const openPortal = async () => {
    if (!user) {
      window.location.href = `/login`
      return
    }

    const { data, error } = await supabase.functions.invoke('create-portal-session')

    if (error || data?.error) {
      throw new Error(data?.error ?? error?.message ?? 'Erreur portail')
    }

    if (!data?.url) throw new Error('URL du portail manquante')
    window.location.href = data.url
  }

  return {
    subscription,
    loading,
    isTrialing,
    isActive,
    isPastDue,
    isCanceled,
    hasAccess,
    trialDaysLeft,
    startCheckout,
    openPortal,
    refetch: fetch,
  }
}

// ─── Helper interne ───────────────────────────────────────────────────────────

function getPriceId(interval: 'month' | 'year'): string {
  const key = interval === 'month'
    ? 'VITE_STRIPE_PRICE_MONTHLY'
    : 'VITE_STRIPE_PRICE_YEARLY'
  const priceId = import.meta.env[key]
  if (!priceId) throw new Error(`Price ID manquant pour ${interval}`)
  return priceId
}
